import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Icone } from '../../composants/icones.tsx';
import { Badge } from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { statutVente } from '../../theme/statuts.ts';
import { imprimerVente } from './imprimer-vente.ts';
import type { DetailVente } from './types.ts';

/** En-tête : référence copiable, statut, actions — « Nouvelle vente » / « Imprimer ». */
export function Entete({ vente }: { vente: DetailVente }): ReactNode {
  const { ouvrir } = useApercuPdf();
  const [copie, setCopie] = useState(false);

  async function copier(): Promise<void> {
    try {
      await navigator.clipboard.writeText(vente.numero);
      setCopie(true);
      setTimeout(() => {
        setCopie(false);
      }, 2000);
    } catch {
      // Presse-papier indisponible (contexte non sécurisé, permission
      // refusée…) : le numéro reste sélectionnable à la main juste à côté.
    }
  }

  return (
    <div className="fiche-entete">
      <div style={{ minWidth: 0 }}>
        <nav aria-label="Fil d’Ariane" className="entete__sous" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Link to="/ventes" style={{ color: 'inherit' }}>
            ← Ventes
          </Link>
          <span>/</span>
          <span>Détail</span>
        </nav>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 6 }}>
          <h1 className="fiche-entete__numero">{vente.numero}</h1>
          <button type="button" className="bouton--discret" onClick={() => void copier()}>
            {copie ? 'Copiée' : 'Copier'}
          </button>
          <Badge statut={statutVente(vente.total, vente.reste_du, vente.statut === 'CONTREPASSEE')} />
        </div>
      </div>

      <div className="fiche-entete__actions">
        <Link to="/ventes/nouvelle" className="bouton--discret">
          <Icone nom="plus" taille={16} /> Nouvelle vente
        </Link>
        <button
          type="button"
          onClick={() => {
            ouvrir(() => imprimerVente(vente));
          }}
        >
          <Icone nom="imprimante" taille={16} /> Imprimer le ticket
        </button>
      </div>

      {/*
        Une vente contrepassée n'est pas effacée : elle reste lisible avec son
        motif. C'est ce qui rend la correction opposable — un enregistrement
        supprimé ne prouve rien.
      */}
      {vente.statut === 'CONTREPASSEE' && (
        <p className="avertissement" style={{ width: '100%' }}>
          Vente contrepassée
          {vente.motif_contrepassation !== null &&
            vente.motif_contrepassation !== undefined &&
            ` — ${vente.motif_contrepassation}`}
        </p>
      )}
      {vente.contrepasse_id !== null && vente.contrepasse_id !== undefined && (
        <p className="avertissement" style={{ width: '100%' }}>
          Cette écriture ANNULE une vente antérieure.
        </p>
      )}
    </div>
  );
}
